import axios from "axios";
import api from "./client";
import { API_BASE_URL } from "../config/env";

/* Onboarding as a workflow (lane D's contract, docs/ONBOARDING_WORKFLOW_CONTRACT
   in crm_backend): templates per role → assign to an employee → send the
   self-serve link → the employee completes REAL steps on their phone (a file, a
   signature, a credential, a training record) → the manager sees who's stuck.
   Plus ad-hoc document requests that land in the card's Docs tab. */

// ── manager side (authed) ──
export const listOnboardingTemplates = (params = {}) => api.get("/people/onboarding/templates/", { params });
export const createOnboardingTemplate = (body) => api.post("/people/onboarding/templates/", body);
export const updateOnboardingTemplate = (id, body) => api.put(`/people/onboarding/templates/${id}/`, body);
export const deleteOnboardingTemplate = (id) => api.delete(`/people/onboarding/templates/${id}/`);

export const listOnboardingAssignments = (params = {}) => api.get("/people/onboarding/assignments/", { params });
export const assignOnboarding = (employee_id, template_id) => api.post("/people/onboarding/assign/", { employee_id, template_id });
export const sendOnboarding = (assignmentId, body = {}) => api.post(`/people/onboarding/assignments/${assignmentId}/send/`, body);
export const completeManagerStep = (assignmentId, stepId, form) =>
  api.post(`/people/onboarding/assignments/${assignmentId}/steps/${stepId}/complete/`, form,
    form instanceof FormData ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);

export const listDocumentRequests = (params = {}) => api.get("/people/documents/request/", { params });
export const requestDocument = (body) => api.post("/people/documents/request/", body);
export const resendDocumentRequest = (id, body = {}) => api.post(`/people/documents/request/${id}/send/`, body);
export const signDocument = (fileUuid, body) => api.post(`/people/documents/${fileUuid}/sign/`, body);

// ── employee side (public, token-gated; no auth headers, no tenant headers) ──
const pub = axios.create({ baseURL: API_BASE_URL, timeout: 60000 });
export const getSelfOnboarding = (token) => pub.get(`/people/onboarding/self/${token}/`);
export const completeSelfStep = (token, stepId, form) =>
  pub.post(`/people/onboarding/self/${token}/steps/${stepId}/complete/`, form,
    form instanceof FormData ? { headers: { "Content-Type": "multipart/form-data" } } : undefined);
export const getDocumentRequestSelf = (token) => pub.get(`/people/documents/request/self/${token}/`);
export const fulfilDocumentRequest = (token, file) => {
  const form = new FormData(); form.append("file", file);
  return pub.post(`/people/documents/request/self/${token}/`, form, { headers: { "Content-Type": "multipart/form-data" } });
};

// ── vocabulary ──
export const STEP_KINDS = [
  { value: "document_upload", label: "Upload a document", hint: "the employee uploads it from their phone" },
  { value: "signature", label: "Read and sign", hint: "a published SOP or handbook, signed by typed name" },
  { value: "credential", label: "License / credential", hint: "linked from the compliance intake" },
  { value: "training", label: "Training", hint: "a course completion, recorded" },
];
export const TEMPLATE_ROLES = [
  { value: "tech", label: "Technician" }, { value: "office", label: "Office" }, { value: "sales", label: "Sales" },
  { value: "manager", label: "Manager" }, { value: "general", label: "General" },
];
export const DOC_TYPES = [
  ["i9", "I-9 (employment eligibility)"], ["w4", "W-4 (tax withholding)"], ["direct_deposit", "Direct deposit form"],
  ["void_check", "Voided check"], ["drivers_license", "Driver’s license"], ["license", "Professional license"],
  ["certification", "Certification"], ["insurance_card", "Insurance card"], ["emergency_contact", "Emergency contact form"],
  ["w9", "W-9"], ["contract", "Employment contract"], ["document", "Other document"],
];
export const docTypeLabel = (key) => (DOC_TYPES.find(([k]) => k === key) || [])[1] || String(key || "document").replace(/_/g, " ");

// A person's CRM role → the template role that fits them.
export const templateRoleFor = (role) => {
  const r = String(role || "").toLowerCase();
  if (/tech|field|instructor/.test(r)) return "tech";
  if (/sales|rep/.test(r)) return "sales";
  if (/manager|mgr|admin|owner/.test(r)) return "manager";
  if (/office|staff|dispatch|csr/.test(r)) return "office";
  return "general";
};

// The standard checklists, built once per tenant on request. Signature steps
// point at SOP slugs that exist; credential steps at the tenant's pack.
export const defaultTemplates = ({ sopSlugs = [], credentialTypes = [] } = {}) => {
  const has = (slug) => sopSlugs.includes(slug);
  const sign = (slug, label) => (has(slug) ? [{ kind: "signature", label, config: { sop_slug: slug } }] : []);
  const hr = [
    { kind: "document_upload", label: "Upload your I-9", config: { doc_type: "i9" } },
    { kind: "document_upload", label: "Upload your W-4", config: { doc_type: "w4" } },
    { kind: "document_upload", label: "Upload a voided check for direct deposit", config: { doc_type: "void_check" } },
    { kind: "document_upload", label: "Upload your driver’s license", config: { doc_type: "drivers_license" } },
    ...sign("employee-handbook", "Read and sign the employee handbook"),
    ...sign("handbook", "Read and sign the employee handbook"),
  ];
  const safety = sign("safety-policy", "Read and sign the safety policy");
  const creds = credentialTypes.map((c) => ({ kind: "credential", label: `License on file: ${c.label || c.type_key}`, assignee: "manager", config: { credential_type_key: c.type_key } }));
  return [
    { role: "tech", name: "Field technician", steps: [...hr, ...safety, ...creds, { kind: "training", label: "Ride-along with a senior tech", assignee: "manager", config: { training_course: "Ride-along" } }] },
    { role: "office", name: "Office", steps: [...hr, { kind: "training", label: "Phones and scheduling walkthrough", assignee: "manager", config: { training_course: "Office systems" } }] },
    { role: "sales", name: "Sales", steps: [...hr, { kind: "training", label: "Pricing and proposals walkthrough", assignee: "manager", config: { training_course: "Sales onboarding" } }] },
  ].map((t) => ({ ...t, steps: t.steps.map((s, i) => ({ order: i, assignee: "employee", required: true, ...s })) }));
};
